// Textos e dados fixos da landing. Precos e beneficios dos planos NAO moram
// aqui: vem de @/lib/billing-plans (fonte unica da UI de billing).

/** Uma cena do demo do hero: o link chega com a tag de outro e sai com a sua. */
export interface HeroScene {
  readonly store: string;
  readonly product: string;
  readonly price: string;
  /** Parte do link que nao muda (pode ser vazia quando a loja gera link novo). */
  readonly linkHead: string;
  /** Trecho que pertence a outro afiliado (ou o link cru inteiro). */
  readonly foreign: string;
  /** Trecho que passa a ser seu depois da troca. */
  readonly own: string;
  /** Como o link foi gerado, em uma linha. */
  readonly how: string;
  /** Comissao simulada (centavos) ou null quando a medicao fica na loja. */
  readonly commissionCents: number | null;
  /** Onde o resultado aparece, quando nao ha contador aqui. */
  readonly whereToTrack: string;
}

export const HERO_SCENES: readonly HeroScene[] = [
  {
    store: "Shopee",
    product: "Fone bluetooth com cancelamento de ruído",
    price: "De R$ 299,00 por R$ 189,90",
    linkHead: "",
    foreign: "shopee.com.br/product/829104772/23918457710",
    own: "s.shopee.com.br/4AqZ8xKpLm",
    how: "Link de afiliado gerado pela API oficial da Shopee.",
    commissionCents: 1240,
    whereToTrack: "Comissão lida do relatório oficial da Shopee.",
  },
  {
    store: "Amazon",
    product: "Air fryer 4 litros, 1500 W",
    price: "De R$ 449,00 por R$ 279,90",
    linkHead: "amazon.com.br/dp/B0CX7K2M9Q?tag=",
    foreign: "outroafiliado-20",
    own: "suatag-20",
    how: "Link oficial da Amazon, sem encurtador no meio.",
    commissionCents: null,
    whereToTrack: "Cliques e comissões ficam no painel do Amazon Associados.",
  },
  {
    store: "Mercado Livre",
    product: "Kit 3 camisetas dry fit",
    price: "De R$ 129,90 por R$ 74,90",
    linkHead: "mercadolivre.com.br/p/MLB19873452?matt_word=",
    foreign: "outrogrupo",
    own: "suatag",
    how: "Sua tag no link oficial do Mercado Livre, sem redirecionamento.",
    commissionCents: null,
    whereToTrack: "Cliques e comissões ficam no painel de afiliados do Mercado Livre.",
  },
];

/** Linhas do manifesto. `mark` e o trecho que recebe o marca-texto. */
export interface ManifestoLine {
  readonly before: string;
  readonly mark: string;
  readonly after: string;
}

export const MANIFESTO: readonly ManifestoLine[] = [
  { before: "Oferta boa circula. Sai de um grupo, cai em outro, é ", mark: "copiada", after: " e repostada." },
  { before: "Em cada parada, o link carrega a ", mark: "tag de quem postou primeiro", after: "." },
  { before: "Quem clica compra. Quem recebe a comissão é ", mark: "o dono da tag", after: "." },
  { before: "O LucraLink troca a tag antes de publicar. A oferta passa a ", mark: "pagar você", after: "." },
];

export interface Step {
  readonly verb: string;
  readonly text: string;
  readonly detail: string;
}

export const STEPS: readonly Step[] = [
  {
    verb: "Conecte",
    text: "Ligue seu Telegram e seu WhatsApp pelo painel. O bot entra nos grupos que você escolher.",
    detail: "@LucraLinkBot",
  },
  {
    verb: "Cadastre suas tags",
    text: "Informe seus IDs de afiliado de cada loja. Chaves de API ficam guardadas no cofre, fora do navegador.",
    detail: "tag=suatag-20",
  },
  {
    verb: "Deixe rodando",
    text: "Defina origens e destinos. Cada oferta que chega sai publicada com o seu link, sem você copiar nada.",
    detail: "origem → seu grupo",
  },
];

export interface StoreGroup {
  readonly title: string;
  readonly text: string;
  readonly stores: readonly string[];
}

export const STORE_GROUPS: readonly StoreGroup[] = [
  {
    title: "Com encurtador e clique rastreado",
    text: "Lojas que permitem link curto. Você acompanha cada clique no painel.",
    stores: ["Shopee", "AliExpress", "SHEIN", "AWIN"],
  },
  {
    title: "Link oficial, direto na loja",
    text: "Lojas que proíbem redirecionamento. O link sai com a sua tag e mais nada no meio.",
    stores: ["Amazon", "Mercado Livre", "Magalu"],
  },
];

export type CompareValue = "sim" | "nao-encontrado" | string;

export interface CompareRow {
  readonly label: string;
  readonly lucralink: CompareValue;
  readonly easyfy: CompareValue;
}

/** Coluna Easyfy: so o que o site publico deles (home, /comparar, planos e blog)
 *  declarava no levantamento de outubro de 2026. Base original de junho/2026 em
 *  docs-internal/easyfy-clone/01-feature-map-easyfy.md. */
export const COMPARE_ROWS: readonly CompareRow[] = [
  { label: "Bot que troca links no Telegram e no WhatsApp", lucralink: "sim", easyfy: "sim" },
  { label: "Shopee, Amazon, Mercado Livre, Magalu, AliExpress, SHEIN e AWIN", lucralink: "sim", easyfy: "sim" },
  { label: "Comissões oficiais da Shopee e da AWIN", lucralink: "sim", easyfy: "sim" },
  { label: "IA para textos e imagens de produto", lucralink: "sim", easyfy: "sim" },
  { label: "Vitrine pública e link na bio", lucralink: "sim", easyfy: "sim" },
  { label: "Calendário e disparos agendados", lucralink: "sim", easyfy: "sim" },
  { label: "Links no plano gratuito", lucralink: "Ilimitados", easyfy: "Ilimitados" },
  { label: "Rodízio de grupos", lucralink: "sim", easyfy: "nao-encontrado" },
  { label: "Encurtador bloqueado por padrão na Amazon, no Mercado Livre e na Magalu", lucralink: "sim", easyfy: "nao-encontrado" },
];

export interface FaqItem {
  readonly q: string;
  readonly a: string;
}

export const FAQ: readonly FaqItem[] = [
  {
    q: "O que o LucraLink faz, na prática?",
    a: "Um bot lê as ofertas que chegam nos grupos de origem que você escolheu, troca o link de cada produto pelo seu link de afiliado e publica nos seus grupos. Também gera links avulsos, encurta, mede cliques e organiza vitrine, disparos e criativos.",
  },
  {
    q: "A comissão é minha mesmo?",
    a: "Sim. O LucraLink cobra a assinatura, não um percentual. A comissão das vendas feitas pelos seus links é paga pela loja direto na sua conta de afiliado.",
  },
  {
    q: "Por que os links da Amazon e do Mercado Livre não ficam encurtados?",
    a: "As regras de afiliados da Amazon, do Mercado Livre e da Magalu proíbem redirecionamento e encurtador de terceiros. Por isso o LucraLink publica o link oficial com a sua tag. Cliques e comissões dessas lojas aparecem no painel de afiliados de cada uma.",
  },
  {
    q: "Toda conversão dá certo?",
    a: "Não, e o painel mostra quando falha. Link de loja não suportada, credencial ausente ou instabilidade da loja impedem a troca. Nesses casos o histórico registra o motivo para você corrigir.",
  },
  {
    q: "O plano Free tem limite?",
    a: "Links de afiliado são ilimitados nas lojas suportadas. A automação de grupos no Free funciona com 1 origem, 1 destino e até 10 promoções automáticas por dia.",
  },
  {
    q: "Preciso de cartão para começar?",
    a: "Não. A conta Free não pede cartão. Ele só entra se você assinar o PRO ou o ELITE.",
  },
  {
    q: "Posso cancelar quando quiser?",
    a: "Sim. O cancelamento é feito pelo painel, sem multa e sem fidelidade. O plano continua ativo até o fim do período já pago.",
  },
  {
    q: "Como ficam minhas chaves e credenciais?",
    a: "Chaves de API ficam guardadas no cofre do banco de dados e nunca chegam ao navegador. O painel mostra apenas uma versão mascarada, e toda a comunicação usa HTTPS.",
  },
];
